import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import Button, { buttonVariants } from 'components/partials/button/button';
import type {
  ButtonSize,
  ButtonVariant,
} from 'components/partials/button/button';
import toast from 'components/partials/toast/toast';

import { renderModal, unmountModal } from 'helpers/modal-render';
import {
  restartCourse,
  startCourse,
} from 'containers/user/learn/containers/content/cources/courses.utils';

import { COURSE_STATUSES } from 'containers/user/learn/containers/content/cources/course.constants';

interface CourseLike {
  id?: string | number;
  name?: string;
  firstLessonId?: string | number;
  status?: string;
}

interface CourseControlButtonsProps {
  course: CourseLike;
  userId?: string;
  size?: ButtonSize;
}

interface ButtonData {
  label: string;
  variant?: ButtonVariant;
  onClick?: () => void;
}

const CourseControlButtons = ({
  course,
  userId,
  size = 'sm',
}: CourseControlButtonsProps): React.JSX.Element => {
  const { id: courseId, name, firstLessonId, status } = course;
  const navigate = useNavigate();

  const getButtonData = (courseStatus?: string): ButtonData => {
    switch (courseStatus) {
      case COURSE_STATUSES.IN_PROGRESS:
        return { label: 'Continue' };
      case COURSE_STATUSES.DONE:
        return {
          label: 'Recap',
          variant: 'reserve-outlined',
        };
      default:
        return {
          onClick: () => {
            startCourse({ userId, courseId });
          },
          label: 'Start Course',
        };
    }
  };

  const { label, onClick } = getButtonData(status);

  const handleRetake = (): void => {
    const targetUrl = `/learn/courses/${courseId}/lesson/${firstLessonId}`;
    const trigger =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const restoreFocus = (): void => {
      if (trigger && document.body.contains(trigger)) {
        trigger.focus();
      }
    };

    const handleCancel = (): void => {
      unmountModal();
      restoreFocus();
    };

    const handleConfirm = async (
      e: React.MouseEvent<HTMLAnchorElement>
    ): Promise<void> => {
      e.preventDefault();

      try {
        await restartCourse({ userId, courseId });
        unmountModal();
        navigate(targetUrl);
      } catch {
        toast.error({ title: 'Failed to retake course. Please try again.' });
      }
    };

    renderModal({
      headerText: `Are you sure you want to retake ${name} course?`,
      text: 'This action will reset your previous progress, and you`ll start from the beginning.',
      disableEnterConfirm: true,
      onCloseCallback: restoreFocus,
      footer: (
        <div className="flex mobile:flex-col-reverse tablet:flex-row justify-end space-y-2 tablet:space-y-0 gap-x-2">
          <Button
            type="button"
            variant="reserve-outlined"
            onClick={handleCancel}
            data-testid="cancel-modal-button"
          >
            Cancel
          </Button>
          <RouterLink
            to={targetUrl}
            onClick={handleConfirm}
            className={classNames(
              buttonVariants({ variant: 'brand-filled' }),
              'inline-flex items-center justify-center no-underline'
            )}
            data-testid="confirm-modal-button"
          >
            Retake Course
          </RouterLink>
        </div>
      ),
    });
  };

  return (
    <div className="flex items-center gap-2">
      {(status === COURSE_STATUSES.DONE ||
        status === COURSE_STATUSES.IN_PROGRESS) && (
        <Button variant="reserve-outlined" size={size} onClick={handleRetake}>
          Retake
        </Button>
      )}

      <RouterLink
        to={`/learn/courses/${courseId}/lesson/${firstLessonId}`}
        onClick={onClick}
        className={classNames(
          buttonVariants({ variant: 'brand-filled', size }),
          'inline-flex items-center justify-center no-underline'
        )}
      >
        {label}
      </RouterLink>
    </div>
  );
};

export default CourseControlButtons;
